import { Box, Typography } from '@mui/material'
import type { MouseEvent } from 'react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useDragPreviewState } from '@/features/bidding/components/cascading/bid-ladder/drag-and-drop/useDragPreviewState'
import BidLadderContextMenu from '@/features/bidding/components/cascading/bid-ladder/shared/BidLadderContextMenu'
import BidLadderInputRowSymmetrical from '@/features/bidding/components/cascading/bid-ladder/symmetrical/BidLadderInputRowSymmetrical'
import type { UsePricingStrategyStateReturn } from '@/features/bidding/hooks/usePricingStrategyState'
import type { VolumeCapacityConfig } from '@/features/bidding/types/bidLadderInput'
import type {
  BidLevelActivationGroupSymmetrical,
  BidLevelInputSymmetrical,
  ContextMenuStateSymmetrical,
  DragPayloadSymmetrical,
} from '@/features/bidding/types/bidLadderInputSymmetrical'

type BidLadderInputSymmetricalProps = {
  levels: BidLevelInputSymmetrical[]
  updatePrice: (levelId: string, price: number | null) => void
  assignGroups: (levelId: string, payload: DragPayloadSymmetrical) => void
  unassignRegulationObjectGroup: (levelId: string, regulationObjectCode: string) => void
  getTotalMWAcrossAllLevels: () => number
  /** Pricing strategy configuration for forecast-based pricing */
  pricingStrategy?: UsePricingStrategyStateReturn
  /** Whether drag and drop is enabled (defaults to true) */
  isDragDropEnabled?: boolean
  /** Called when dragging starts from a bar in the ladder */
  onDragStartFromLadder?: (payload: DragPayloadSymmetrical) => void
  /** Called when dragging ends from a bar in the ladder */
  onDragEndFromLadder?: () => void
  /** Called when clicking forecast chip to open pricing configuration in sidebar */
  onOpenPricingConfiguration?: () => void
  /** Update volume capacity configuration for a level */
  updateVolumeCapacity?: (levelId: string, config: VolumeCapacityConfig) => void
  /** Number of decimal places allowed when mode is 'fixed' */
  fixedModeDecimalPlaces?: number
}

function BidLadderInputSymmetrical({
  levels,
  updatePrice,
  assignGroups,
  unassignRegulationObjectGroup,
  getTotalMWAcrossAllLevels,
  pricingStrategy,
  isDragDropEnabled = true,
  onDragStartFromLadder,
  onDragEndFromLadder,
  onOpenPricingConfiguration,
  updateVolumeCapacity,
  fixedModeDecimalPlaces,
}: Readonly<BidLadderInputSymmetricalProps>) {
  const { t } = useTranslation()

  const [contextMenu, setContextMenu] = useState<ContextMenuStateSymmetrical>({
    anchorPosition: null,
    group: null,
    levelId: null,
  })

  // Track which group should be focused after keyboard move
  const [focusedGroupKey, setFocusedGroupKey] = useState<string | null>(null)

  const clearFocusedGroupKey = useCallback(() => {
    setFocusedGroupKey(null)
  }, [])

  // Drag preview state management
  const {
    handleDragStartFromBar,
    handleDragEndFromBar,
    handleDragOverRow,
    handleDragLeaveRow,
    handleDrop,
    getPreviewGroupsForLevel,
  } = useDragPreviewState<DragPayloadSymmetrical>({
    levels,
    assignGroups,
    onDragStartFromLadder,
    onDragEndFromLadder,
  })

  // Total MW across ALL levels (used for relative bar width comparison)
  const totalMWAcrossAll = getTotalMWAcrossAllLevels()

  const handleContextMenu = useCallback(
    (event: MouseEvent, group: BidLevelActivationGroupSymmetrical, levelId: string) => {
      event.preventDefault()
      setContextMenu({
        anchorPosition: { top: event.clientY, left: event.clientX },
        group,
        levelId,
      })
    },
    [],
  )

  const handleContextMenuClose = useCallback(() => {
    setContextMenu({
      anchorPosition: null,
      group: null,
      levelId: null,
    })
  }, [])

  const handleRemoveGroup = useCallback(() => {
    if (contextMenu.levelId && contextMenu.group) {
      unassignRegulationObjectGroup(contextMenu.levelId, contextMenu.group.regulationObjectCode)
    }
  }, [contextMenu.levelId, contextMenu.group, unassignRegulationObjectGroup])

  // Handle keyboard-based moves (arrow keys)
  const handleKeyboardMove = useCallback(
    (sourceLevelId: string, group: BidLevelActivationGroupSymmetrical, moveDirection: 'up' | 'down') => {
      // Find source level index
      const sourceIndex = levels.findIndex((l) => l.id === sourceLevelId)
      if (sourceIndex === -1) return

      // Calculate target level index
      // 'up' means higher level (higher index in our array)
      // 'down' means lower level (lower index in our array)
      const targetIndex = moveDirection === 'up' ? sourceIndex + 1 : sourceIndex - 1

      // Check bounds
      if (targetIndex < 0 || targetIndex >= levels.length) return

      const targetLevelId = levels[targetIndex].id

      // Get all groups from the same regulation object
      const sourceLevel = levels[sourceIndex]
      const relatedGroups = sourceLevel.assignedGroups.filter(
        (g) => g.regulationObjectCode === group.regulationObjectCode,
      )

      // Create payload and move
      const payload: DragPayloadSymmetrical = {
        regulationObjectCodes: [group.regulationObjectCode],
        activationGroups: [],
        sourceLevelId,
        previewGroups: relatedGroups,
      }
      assignGroups(targetLevelId, payload)

      // Set focus key to maintain focus on the moved group
      // Format: levelId-regulationObjectCode
      setFocusedGroupKey(`${targetLevelId}-${group.regulationObjectCode}`)
    },
    [levels, assignGroups],
  )

  // Validate price ordering: prices should increase as you go up the levels
  // Level 1 (bottom) price <= Level 2 price <= ... <= Level 6 (top) price
  const getPriceValidationError = useCallback(
    (levelIndex: number): boolean => {
      const currentPrice = levels[levelIndex].price

      // No error if price is not set
      if (currentPrice === null) return false

      // Check if price is greater than the level above (higher index = higher level)
      if (levelIndex < levels.length - 1) {
        const abovePrice = levels[levelIndex + 1].price
        if (abovePrice !== null && currentPrice > abovePrice) {
          return true
        }
      }

      // Check if price is less than the level below (lower index = lower level)
      if (levelIndex > 0) {
        const belowPrice = levels[levelIndex - 1].price
        if (belowPrice !== null && currentPrice < belowPrice) {
          return true
        }
      }

      return false
    },
    [levels],
  )

  // Reverse levels for display (level 1 at bottom, level 6 at top)
  const reversedLevels = [...levels].reverse()

  return (
    <Box sx={{ width: '100%' }}>
      {/* Column Headers */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 0.5,
          py: 1,
          backgroundColor: 'action.hover',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ width: 24, textAlign: 'center' }}>
          <Typography
            color="text.secondary"
            sx={{ fontSize: '0.75rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
            variant="caption"
          >
            #
          </Typography>
        </Box>
        <Box sx={{ flex: 1, textAlign: 'center' }}>
          <Typography color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }} variant="caption">
            Groups
          </Typography>
        </Box>
        <Box sx={{ width: 110, textAlign: 'center' }}>
          <Typography
            color="text.secondary"
            sx={{ fontSize: '0.75rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
            variant="caption"
          >
            €/MW
          </Typography>
        </Box>
      </Box>

      {/* Bid Level Rows */}
      <Box
        aria-label={t('bidding.bidding_cascading.bid_ladder.levels_list')}
        role="list"
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {reversedLevels.map((level, displayIndex) => {
          // Calculate the original index (for validation) and display number
          const originalIndex = levels.length - 1 - displayIndex
          const levelNumber = originalIndex + 1 // Level 1 is at the bottom

          // Get pricing config for this level
          const levelPricingConfig = pricingStrategy?.config.levels[originalIndex]
          const isPricingFocused = pricingStrategy?.focusedLevelIndex === originalIndex

          return (
            <BidLadderInputRowSymmetrical
              key={level.id}
              fixedModeDecimalPlaces={fixedModeDecimalPlaces}
              focusedGroupKey={focusedGroupKey}
              isPricingFocused={isPricingFocused}
              level={level}
              levelNumber={levelNumber}
              previewGroups={getPreviewGroupsForLevel(level.id)}
              priceError={getPriceValidationError(originalIndex)}
              pricingConfig={levelPricingConfig}
              totalMWAcrossAll={totalMWAcrossAll}
              onClearFocusedGroupKey={clearFocusedGroupKey}
              onContextMenu={handleContextMenu}
              onDragEndFromBar={isDragDropEnabled ? handleDragEndFromBar : undefined}
              onDragLeaveRow={isDragDropEnabled ? handleDragLeaveRow : undefined}
              onDragOverRow={isDragDropEnabled ? handleDragOverRow : undefined}
              onDragStartFromBar={isDragDropEnabled ? handleDragStartFromBar : undefined}
              onDrop={handleDrop}
              onKeyboardMove={isDragDropEnabled ? handleKeyboardMove : undefined}
              onPriceChange={updatePrice}
              onPricingFocus={
                onOpenPricingConfiguration
                  ? () => {
                      pricingStrategy?.setFocusedLevelIndex(originalIndex)
                      onOpenPricingConfiguration()
                    }
                  : undefined
              }
              onVolumeCapacityChange={updateVolumeCapacity}
            />
          )
        })}
      </Box>

      {/* Context Menu - reuse the existing one with adapted group type */}
      <BidLadderContextMenu
        state={{
          anchorPosition: contextMenu.anchorPosition,
          group: contextMenu.group
            ? {
                ...contextMenu.group,
                direction: 'up' as const, // Symmetrical markets treat all groups as 'up' for menu purposes
              }
            : null,
          levelId: contextMenu.levelId,
        }}
        onClose={handleContextMenuClose}
        onRemove={handleRemoveGroup}
      />
    </Box>
  )
}

export default BidLadderInputSymmetrical
